// src/components/ui/PlatformChip.jsx
import React from 'react';
import { Chip, useTheme, alpha, Box, styled } from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import { Snapchat, TikTok } from './CustomIcons';

// Enhanced Platform configuration with colors and icons
const PLATFORM_CONFIG = {
  facebook: {
    color: '#1877F2',
    darkColor: '#0e5fc0',
    icon: <FacebookIcon fontSize="small" />,
    gradient: 'linear-gradient(135deg, #18ADFF, #1877F2)'
  },
  instagram: {
    color: '#E1306C',
    darkColor: '#c13584',
    icon: <InstagramIcon fontSize="small" />,
    gradient: 'linear-gradient(45deg, #f09433, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'
  },
  twitter: {
    color: '#1DA1F2',
    darkColor: '#0c85d0',
    icon: <TwitterIcon fontSize="small" />,
    gradient: 'linear-gradient(135deg, #1DA1F2, #0c7abf)'
  },
  linkedin: {
    color: '#0077B5',
    darkColor: '#005582',
    icon: <LinkedInIcon fontSize="small" />,
    gradient: 'linear-gradient(135deg, #0077B5, #00557c)'
  },
  youtube: {
    color: '#FF0000',
    darkColor: '#cc0000',
    icon: <YouTubeIcon fontSize="small" />,
    gradient: 'linear-gradient(135deg, #FF0000, #cc0000)'
  },
  tiktok: {
    color: '#000000',
    darkColor: '#666666', // Use a lighter color for dark mode
    icon: <TikTok fontSize="small" />,
    gradient: 'linear-gradient(135deg, #25F4EE, #000000, #FE2C55)',
    darkModeColor: '#25F4EE' // Cyan color for better visibility in dark mode
  },
  snapchat: {
    color: '#FFFC00',
    darkColor: '#ccca00',
    icon: <Snapchat fontSize="small" />,
    gradient: 'linear-gradient(135deg, #FFFC00, #F5CB00)',
    // Special text and border colors for better contrast
    textColor: '#000000',
    borderColor: '#E6E200'
  },
};

// Styled component for platform icon wrapper
const PlatformIconWrapper = styled(Box)(({ theme, platform }) => {
  const platformInfo = PLATFORM_CONFIG[platform] || { color: '#9e9e9e' };
  const isSnapchat = platform === 'snapchat';
  const isTikTok = platform === 'tiktok';
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
    // Special handling for platforms with contrast issues
    background:
      isSnapchat ? '#000000' :
      (isTikTok && isDarkMode) ? '#25F4EE' : // Cyan background for TikTok in dark mode
      platformInfo.gradient || platformInfo.color,
    // Special handling for icon colors
    color:
      isSnapchat ? platformInfo.color :
      (isTikTok && isDarkMode) ? '#000000' : // Black icon on cyan for TikTok in dark mode
      'white',
    // Enhanced outline for better visibility
    boxShadow: isDarkMode
      ? `0 0 0 1.5px ${alpha(platformInfo.color, 0.4)}, 0 0 4px ${alpha(platformInfo.color, 0.3)}`
      : `0 0 0 1px ${alpha(platformInfo.color, 0.2)}`,
    '& svg': {
      fontSize: '0.75rem',
    },
  };
});

/**
 * An enhanced platform chip component with elegant styling
 *
 * @param {Object} props Component props
 * @param {String} props.platform Platform identifier ('facebook', 'instagram', etc)
 * @param {Boolean} props.selected Whether this platform is selected
 * @param {Function} props.onClick Click handler function
 * @param {String} props.size Chip size ('small' or 'medium')
 * @param {String} props.variant Style variant ('default', 'outlined', 'filled', 'gradient')
 * @param {Object} props.sx Additional styles to apply
 */
const PlatformChip = ({
  platform,
  selected = false,
  onClick,
  size = 'small',
  variant = 'default',
  ...props
}) => {
  const theme = useTheme();

  // Get platform configuration or use defaults
  const platformInfo = PLATFORM_CONFIG[platform] || {
    color: '#9e9e9e',
    darkColor: '#757575',
    icon: null,
    gradient: 'linear-gradient(135deg, #9e9e9e, #757575)'
  };

  // Format display label
  const label = platform?.charAt(0)?.toUpperCase() + platform?.slice(1) || 'Platform';

  // Custom icon with platform-colored background
  const customIcon = platformInfo.icon ? (
    <PlatformIconWrapper platform={platform}>
      {React.cloneElement(platformInfo.icon, { fontSize: 'inherit' })}
    </PlatformIconWrapper>
  ) : null;

  // Style variants
  const getVariantStyles = () => {
    // Special handling for platforms with contrast issues
    const isSnapchat = platform === 'snapchat';
    const isTikTok = platform === 'tiktok';
    const isDarkMode = theme.palette.mode === 'dark';
    const isLightMode = !isDarkMode;

    // Text color with special handling for platforms in dark mode
    const textColor =
      platformInfo.textColor ||
      (isTikTok && isDarkMode) ? platformInfo.darkModeColor || '#25F4EE' :
      (isLightMode ? platformInfo.darkColor : 'white');

    // Border color with improved visibility in dark mode
    const borderColor = platformInfo.borderColor || platformInfo.color;

    // Enhanced visibility for TikTok in dark mode
    const bgColor = (isTikTok && isDarkMode) ? '#25F4EE' : platformInfo.color;

    switch (variant) {
      case 'gradient':
        return {
          background: platformInfo.gradient,
          // Special handling for better text readability
          color: isSnapchat ? '#000000' : 'white',
          fontWeight: 500,
          border: 'none',
          // Add outline for better visibility in both modes
          boxShadow: isDarkMode
            ? `0 0 0 1px ${alpha(borderColor, 0.3)}, 0 1px 3px ${alpha(borderColor, 0.4)}`
            : `0 0 0 1px ${alpha(borderColor, 0.1)}, 0 1px 2px ${alpha(borderColor, 0.2)}`,
          '&:hover': {
            boxShadow: `0 2px 5px ${alpha(platformInfo.color, isDarkMode ? 0.6 : 0.5)}`,
          },
        };
      case 'filled':
        return {
          bgcolor: isDarkMode && isTikTok ? bgColor : platformInfo.color,
          // Special handling for text readability
          color: (isSnapchat || (isDarkMode && isTikTok)) ? '#000000' : 'white',
          fontWeight: 500,
          border: 'none',
          // Add outline for better visibility in both modes
          boxShadow: isDarkMode
            ? `0 0 0 1px ${alpha(borderColor, 0.3)}, 0 1px 3px ${alpha(borderColor, 0.25)}`
            : `0 0 0 1px ${alpha(borderColor, 0.1)}`,
          '&:hover': {
            bgcolor: isDarkMode && isTikTok
              ? alpha(bgColor, 0.9)
              : platformInfo.darkColor,
          },
        };
      case 'outlined':
        return {
          bgcolor: 'transparent',
          color: isDarkMode
            ? isTikTok
              ? platformInfo.darkModeColor || '#25F4EE'
              : platformInfo.color
            : platformInfo.darkColor,
          border: `1.5px solid ${isDarkMode ? borderColor : alpha(borderColor, 0.7)}`,
          fontWeight: isDarkMode ? 600 : 500,
          '&:hover': {
            bgcolor: alpha(platformInfo.color, isDarkMode ? 0.15 : 0.08),
          },
        };
      default:
        return {
          // Increased alpha values for better contrast in both modes
          bgcolor: alpha(platformInfo.color, isLightMode ? 0.15 : 0.2),
          color: isTikTok && isDarkMode
            ? '#25F4EE'
            : textColor,
          border: `1px solid ${alpha(borderColor, isLightMode ? 0.3 : 0.4)}`,
          fontWeight: isDarkMode ? 600 : 500,
          '&:hover': {
            bgcolor: alpha(platformInfo.color, isLightMode ? 0.2 : 0.25),
            borderColor: alpha(borderColor, isLightMode ? 0.4 : 0.5),
          },
        };
    }
  };

  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Chip
      label={label}
      avatar={customIcon}
      onClick={onClick}
      size={size}
      sx={{
        ...getVariantStyles(),
        borderRadius: '16px',
        boxShadow: variant === 'gradient'
          ? isDarkMode
            ? '0 2px 5px rgba(0,0,0,0.2)'
            : '0 1px 3px rgba(0,0,0,0.12)'
          : 'none',
        transition: theme.aghrba?.tokens?.transitions?.standard || 'all 0.2s ease-in-out',
        backdropFilter: 'blur(8px)',
        '& .MuiChip-label': {
          fontWeight: 'inherit',
          px: 1,
          // Add subtle text shadow for better readability in dark mode
          ...(isDarkMode && variant !== 'outlined' && platform !== 'snapchat' && {
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          }),
        },
        // Add focus state for keyboard navigation
        '&:focus': {
          outline: isDarkMode
            ? `2px solid ${alpha(platformInfo.color, 0.5)}`
            : `2px solid ${alpha(platformInfo.color, 0.3)}`,
          outlineOffset: 1,
        },
        ...props.sx
      }}
      {...props}
    />
  );
};

export default PlatformChip;